import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Site, SiteInput, SiteStatus, User } from '../generated/gql-types';
import { upsertOne } from '../util/zustand.util';
import { siteActions } from './site.action';

const SITE_FETCH_BATCH_SIZE = 10;

interface SiteState {
  sites: Site[];
  isLoading: boolean;
  error: string | null;
  fetchSitesForClient: (
    clientId: string,
    status?: SiteStatus[],
  ) => Promise<Site[]>;
  fetchSitesForClients: (clientIds: string[]) => Promise<Site[]>;
  createSite: (
    details: SiteInput,
  ) => Promise<{ success: boolean; site?: Site; error?: string }>;
  updateSite: (
    id: string,
    details: SiteInput,
  ) => Promise<{ success: boolean; site?: Site; error?: string }>;
  setSiteStatus: (
    id: string,
    status: SiteStatus,
  ) => Promise<{ success: boolean; site?: Site; error?: string }>;
  setAccountsForSite: (
    siteId: string,
    clientUserIds?: string[],
    inviteIds?: string[],
  ) => Promise<{ success: boolean; users?: User[]; error?: string }>;
  setSitesForAccount: (params: {
    clientUserId?: string;
    inviteId?: string;
    siteIds: string[];
  }) => Promise<{ success: boolean; siteIds?: string[]; error?: string }>;
  getSitesForClient: (clientId: string) => Site[];
}

const validateSiteInput = (details: SiteInput): string | null => {
  if (!details.name?.trim()) {
    return 'Site name is required';
  }
  if (!details.clientId?.trim()) {
    return 'Client ID is required';
  }
  return null;
};

const fetchSitesInBatches = async (
  clientIds: string[],
): Promise<{ sites: Site[]; fetchedClientIds: Set<string> }> => {
  const fetchedSites: Site[] = [];
  const fetchedClientIds = new Set<string>();

  for (
    let startIndex = 0;
    startIndex < clientIds.length;
    startIndex += SITE_FETCH_BATCH_SIZE
  ) {
    const batch = clientIds.slice(
      startIndex,
      startIndex + SITE_FETCH_BATCH_SIZE,
    );
    const batchResults = await Promise.all(
      batch.map(async (clientId) => {
        try {
          const sites = await siteActions.getSitesForClient(clientId);
          return { clientId, sites, fetched: true };
        } catch (error) {
          console.error(`Error fetching sites for client ${clientId}:`, error);
          return { clientId, sites: [] as Site[], fetched: false };
        }
      }),
    );

    for (const result of batchResults) {
      if (!result.fetched) continue;
      fetchedClientIds.add(result.clientId);
      fetchedSites.push(...result.sites);
    }
  }

  return { sites: fetchedSites, fetchedClientIds };
};

const sanitizeSiteInput = (details: SiteInput): SiteInput => ({
  ...details,
  name: details.name?.trim(),
  clientId: details.clientId?.trim(),
  description: details.description?.trim(),
  contactNumber: details.contactNumber?.trim(),
  contacts: details.contacts?.map((contact) => ({
    name: contact.name?.trim(),
    email: contact.email?.trim(),
    phone: contact.phone?.trim(),
  })),
  address: details.address
    ? {
        ...details.address,
        addressLine1: details.address.addressLine1?.trim(),
        addressLine2: details.address.addressLine2?.trim(),
        suburb: details.address.suburb?.trim(),
        state: details.address.state?.trim(),
        postcode: details.address.postcode?.trim(),
        countryLabel: details.address.countryLabel?.trim(),
        countryValue: details.address.countryValue?.trim(),
      }
    : undefined,
});

export const useSiteStore = create<SiteState>()(
  persist(
    (set, get) => ({
      sites: [],
      isLoading: false,
      error: null,
      fetchSitesForClient: async (clientId: string, status?: SiteStatus[]) => {
        try {
          set({ isLoading: true, error: null });
          const fetchedSites = await siteActions.getSitesForClient(
            clientId,
            status,
          );

          const otherSites = get().sites.filter(
            (site) => site.clientId !== clientId,
          );
          set({
            sites: [...otherSites, ...fetchedSites],
            isLoading: false,
          });
          return fetchedSites;
        } catch (error) {
          console.error('Error fetching sites for client:', error);
          set({ error: 'Failed to fetch sites', isLoading: false });
          return [];
        }
      },
      fetchSitesForClients: async (clientIds: string[]) => {
        const uniqueClientIds = [
          ...new Set(
            clientIds
              .map((clientId) => clientId.trim())
              .filter((clientId) => clientId.length > 0),
          ),
        ];
        if (uniqueClientIds.length === 0) return [];

        try {
          set({ isLoading: true, error: null });
          const { sites: fetchedSites, fetchedClientIds } =
            await fetchSitesInBatches(uniqueClientIds);

          // Filter out sites that we just fetched to avoid duplicates.
          const otherSites = get().sites.filter(
            (site) => !fetchedClientIds.has(site.clientId),
          );

          // Combine the new sites with the existing sites.
          set({
            sites: [...otherSites, ...fetchedSites],
            isLoading: false,
          });
          return fetchedSites;
        } catch (error) {
          console.error('Error fetching sites for clients:', error);
          set({ error: 'Failed to fetch sites', isLoading: false });
          return [];
        }
      },
      createSite: async (details: SiteInput) => {
        const validationError = validateSiteInput(details);
        if (validationError) {
          return { success: false, error: validationError };
        }

        try {
          set({ isLoading: true, error: null });
          const result = await siteActions.createSite(
            sanitizeSiteInput(details),
          );

          if (!result.success || !result.site) {
            set({
              error: result.error ?? 'Failed to create site',
              isLoading: false,
            });
            return result;
          }

          set((state) => ({
            sites: [result.site!, ...state.sites],
            isLoading: false,
          }));
          return result;
        } catch (error) {
          console.error('Error creating site:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },
      updateSite: async (id: string, details: SiteInput) => {
        if (!id?.trim()) {
          return { success: false, error: 'Site ID is required' };
        }

        try {
          set({ isLoading: true, error: null });
          const result = await siteActions.updateSite(
            id,
            sanitizeSiteInput(details),
          );

          if (!result.success || !result.site) {
            set({
              error: result.error ?? 'Failed to update site',
              isLoading: false,
            });
            return result;
          }

          set((state) => ({
            sites: upsertOne(state.sites, result.site!),
            isLoading: false,
          }));
          return result;
        } catch (error) {
          console.error('Error updating site:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },
      setSiteStatus: async (id: string, status: SiteStatus) => {
        if (!id?.trim()) {
          return { success: false, error: 'Site ID is required' };
        }

        try {
          set({ isLoading: true, error: null });
          const result = await siteActions.setSiteStatus(id, status);

          if (!result.success || !result.site) {
            set({
              error: result.error ?? 'Failed to set site status',
              isLoading: false,
            });
            return result;
          }

          set((state) => ({
            sites: upsertOne(state.sites, result.site!),
            isLoading: false,
          }));
          return result;
        } catch (error) {
          console.error('Error setting site status:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },
      setAccountsForSite: async (
        siteId: string,
        clientUserIds?: string[],
        inviteIds?: string[],
      ) => {
        if (!siteId?.trim()) {
          return { success: false, error: 'Site ID is required' };
        }

        if (!clientUserIds && !inviteIds) {
          return {
            success: false,
            error: 'At least one of users or invites is required',
          };
        }

        try {
          set({ isLoading: true, error: null });
          const result = await siteActions.setAccountsForSite(
            siteId,
            clientUserIds,
            inviteIds,
          );

          if (!result.success) {
            set({
              error: result.error ?? 'Failed to update site accounts',
              isLoading: false,
            });
            return result;
          }

          set({ isLoading: false });
          return result;
        } catch (error) {
          console.error('Error updating site accounts:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },
      setSitesForAccount: async (params: {
        clientUserId?: string;
        inviteId?: string;
        siteIds: string[];
      }) => {
        if (!params.clientUserId && !params.inviteId) {
          return {
            success: false,
            error: 'A user or invite is required',
          };
        }

        try {
          set({ isLoading: true, error: null });
          const result = await siteActions.setSitesForAccount(params);

          if (!result.success) {
            set({
              error: result.error ?? 'Failed to update site access',
              isLoading: false,
            });
            return result;
          }

          set({ isLoading: false });
          return result;
        } catch (error) {
          console.error('Error updating site access:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },
      getSitesForClient: (clientId: string) =>
        get().sites.filter((site) => site.clientId === clientId),
    }),
    {
      name: 'site-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        sites: state.sites,
      }),
    },
  ),
);
